import { LuLoaderCircle } from "react-icons/lu";
import { Tooltip } from "react-tooltip";
import darvinAutoFixAnim from "../../../assets/darvin/darvin_auto_fix.webp";
import darvinTypingAnim from "../../../assets/darvin/darvin_typing.webp";
import { ProjectModalProvider } from "../../../providers/ProjectModalProvider";
import {
  DashboardMode,
  useDashboardMode,
  useWorkspaceSection,
  WorkspaceSection,
} from "../../../view_stores/useDashboardControlsStore";
import { useProjectName } from "../../../view_stores/useProjectConfigStore";
import { useProjectPreviewUrl } from "../../../view_stores/useProjectPreviewStore";
import {
  ProjectStatus,
  useIsProjectProcessing,
  useProjectStatus,
} from "../../../view_stores/useProjectStatusStore";
import { SplitPanel } from "../../common_components/SplitPanel";
import { ProjectFiles } from "../../projectFiles/ProjectFiles";
import { ProjectPreview } from "../../projectPreview/ProjectPreview";
import ProjectDashboardTopPanel from "./ProjectDashboardTopPanel";
import { ProjectWorkspaceContent } from "./ProjectWorkspaceContent";
import { ProjectWorkspaceMenu } from "./ProjectWorkspaceMenu";

export function ProjectDashboard() {
  const isProjectProcessing = useIsProjectProcessing();
  const projectName = useProjectName();
  const previewUrl = useProjectPreviewUrl();

  const hasNeededData = projectName && previewUrl;
  const isDisabled = !hasNeededData;

  return (
    <div
      className="h-full w-full flex flex-col relative
      bg-light-surface-primary dark:bg-dark-surface-tertiary"
    >
      <ProjectModalProvider>
        <ProjectDashboardTopPanel isDisabled={isDisabled} />
        {!isDisabled && (
          <div className="flex-1 overflow-hidden w-full">
            <Content />
          </div>
        )}
        {isProjectProcessing && !previewUrl && <ProcessingInfo />}
        <Tooltip
          id="dashboard-tooltip"
          className="max-w-[500px]"
          place="top"
          opacity={1.0}
          clickable={true}
        />
      </ProjectModalProvider>
    </div>
  );
}

function Content() {
  const dashboardMode = useDashboardMode();
  const workspaceSection = useWorkspaceSection();

  if (dashboardMode === DashboardMode.WORKSPACE) {
    if (workspaceSection === WorkspaceSection.CODE) {
      return <ProjectFiles />;
    }

    return (
      <SplitPanel
        leftChild={<ProjectWorkspaceMenu />}
        rightChild={<ProjectWorkspaceContent />}
        initialLeftChildWidth={25}
        minRightChildWidth={300}
      />
    );
  }

  return <ProjectPreview />;
}

function ProcessingInfo() {
  const projectName = useProjectName();
  const projectStatus = useProjectStatus();

  if (!projectName || projectStatus === ProjectStatus.JOB_DONE) {
    return null;
  }

  const getProcessingContent = (): {
    animation: React.ReactNode;
    text: string;
  } => {
    if (projectStatus === ProjectStatus.FIXING_COMPILATION_ERRORS) {
      return {
        animation: (
          <img src={darvinAutoFixAnim} className="h-[35vh] max-h-[300px]" />
        ),
        text: "Darvin is working on your app",
      };
    } else if (projectStatus === ProjectStatus.CREATING_FIRST_VERSION) {
      return {
        animation: (
          <img src={darvinTypingAnim} className="h-[35vh] max-h-[300px]" />
        ),
        text: "Darvin is working on your app",
      };
    }
    return {
      animation: <LuLoaderCircle size={24} className="animate-spin" />,
      text: "Loading",
    };
  };

  const getTooltipMessage = () => {
    if (
      projectStatus === ProjectStatus.FIXING_COMPILATION_ERRORS ||
      projectStatus === ProjectStatus.CREATING_FIRST_VERSION
    ) {
      const messages = [
        "Be specific: describe the exact change you want to see.",
        "Break big ideas into steps — one change at a time works best.",
        "Share context: say what your app does now and what you're aiming for.",
        'Set constraints upfront: "must work offline" or "no sign-in required."',
        "Include visual examples — upload a screenshot to guide design and layout.",
        'Avoid vague language: "center the button" works better than "make it nicer."',
        "Mention priorities: is design, speed, or simplicity most important right now?",
      ];
      return (
        <p className="px-4 text-center text-lg mt-4">
          💡 Hint: {messages[Math.floor(Math.random() * messages.length)]}
        </p>
      );
    }
    return null;
  };

  const { animation, text } = getProcessingContent();

  return (
    <div
      className="flex flex-col items-center justify-center gap-4 
    absolute top-0 left-0 
    w-full h-full
    bg-black/10 dark:bg-black/25"
    >
      {animation}
      <p className="text-lg">{text}</p>
      {getTooltipMessage()}
    </div>
  );
}
